import React, { useMemo } from 'react';
import {
    MeInfoResponse,
    MessageLinkEntityType,
    ProviderClaim,
    ProviderCoding,
    ProviderComment,
    ProviderMessage,
    SupportClaim,
    SupportCoding,
    SupportComment,
    SupportMessage,
} from '../../api';

async function noop(d?: any): Promise<void> {
    return undefined;
}

async function noopClaimSearch(term: string): Promise<(ProviderClaim | SupportClaim)[]> {
    return [];
}

export interface IComment {
    messageId: string;
    body: string;
    note: boolean;
    files?: File[];
}

export interface IMessage {
    subject: string;
    body: string;
    note: boolean;
    isPrivateNote?: boolean;
    claimId?: string;
    files?: File[];
    priority?: string;
    dueDate?: string;
    links?: {
        claims?: Array<{ id: string; entityType: string }>;
        [key: string]: Array<{ id: string; entityType: string }> | undefined;
    };
}

export interface IDocument {
    id?: string;
    filename?: string;
    description?: string;
    width?: number;
    height?: number;
    filesize?: number;
    notes?: string;
    mimetype?: string;
    thumbnail?: string;
    claimId?: string;
    active?: boolean;
}

export interface IMessagingContextProps {
    isReadOnly?: boolean;
    isSubjectEditable?: boolean;
    isListLoading?: boolean;
    isDetailLoading?: boolean;
    enableMessageTypeValidation: boolean;
    isReadOnlyClaimChoice?: boolean;

    account: MeInfoResponse | undefined;
    message: ProviderMessage | SupportMessage | undefined;
    providerMessage?: ProviderMessage | undefined;
    comments: (ProviderComment | SupportComment)[] | undefined;
    documents: IDocument[] | undefined;
    claims: (ProviderClaim | SupportClaim)[] | undefined;

    getCategoryName?: (categoryId: string) => string | undefined;
    getUserName?: (userId: string) => string | undefined;
    /**
     * Resolve the claims a task links to, when the host app can answer better than a fetch.
     *
     * `ClaimCard` otherwise calls `listProviderClaims` itself. In demo mode that request is refused
     * at the boundary, the catch empties the list, and the panel reports "No claims linked" beside a
     * row that is plainly showing a Claim ID — a REQUEST failure rendered as a statement about the
     * data (NS-3). The provider app passes a resolver that goes through `claimStore`, which already
     * branches on `features.isDemo`.
     *
     * Optional: the support console does not supply one and keeps the fetch.
     */
    onClaimsResolve?: (claimIds: string[]) => Promise<(ProviderClaim | SupportClaim)[]>;
    /**
     * Resolve the codings a task links to, when the host app can answer better than a request — FE-81.
     *
     * `CodingReasonCards` otherwise fetches each coding through the API client, which demo mode
     * refuses — so a halted demo task showed no rules and none of the halt behind its Resolve options.
     * The provider app passes the generated codings in demo mode only.
     *
     * Optional: without it the card keeps its own fetch, which is what every real account uses.
     */
    onCodingsResolve?: (codingIds: string[]) => Promise<(ProviderCoding | SupportCoding)[]>;
    onClaimSearch: (term: string) => Promise<(ProviderClaim | SupportClaim)[]>;
    onCommentToggle: (mid: string, cid: string, note: boolean) => Promise<void>;
    onComposeComment: (comment: IComment) => Promise<void>;
    onComposeMessage: (message: IMessage) => Promise<void | string>;
    onDocumentDownload: (documentId: string) => Promise<void>;
    onDocumentActualDownload?: (documentId: string) => Promise<string>;
    onDocumentAssign: (document: IDocument) => Promise<void>;
    onDocumentEdit?: (document: IDocument) => Promise<void>;
    onMarkAsClosed: (ids: string | string[]) => Promise<void>;
    onMarkAsOpen: (ids: string | string[]) => Promise<void>;
    onMarkAsRead: (ids: string | string[]) => Promise<void>;
    onMarkAsUnread: (ids: string | string[]) => Promise<void>;
    onProviderMarkAsClosed?: (id: string) => Promise<void>;
    onProviderMarkAsOpen?: (id: string) => Promise<void>;
    onMessageClose: () => Promise<void>;
    onMessageOpen: (message: ProviderMessage | SupportMessage) => Promise<void>;
    /** Mark message done and open the next inbox message (or close panel if none). */
    onCloseAndGoToNext?: (id: string) => Promise<void>;
    onSubjectUpdate: (id: string, subject: string) => Promise<void>;
    onClaimUpdate: (id: string, claimId: string | null) => Promise<void>;
    onLinkEntityToMessage?: (id: string, entityType: MessageLinkEntityType, entityIds: string[]) => Promise<void>;
    onUnlinkEntityFromMessage?: (id: string, entityType: MessageLinkEntityType, entityIds: string[]) => Promise<void>;
    onUpdateMessage?: (id: string, payload: { priority?: string; dueDate?: string | null }) => Promise<void>;
    onMessageFieldsUpdate?: () => void;
}

export interface IMessageProviderProps {
    children: React.ReactNode;

    isReadOnly?: boolean;
    isSubjectEditable?: boolean;
    isListLoading?: boolean;
    isDetailLoading?: boolean;
    enableMessageTypeValidation: boolean;
    isReadOnlyClaimChoice?: boolean;

    account: MeInfoResponse | undefined;
    message: ProviderMessage | SupportMessage | undefined;
    providerMessage?: ProviderMessage | undefined;
    comments: (ProviderComment | SupportComment)[] | undefined;
    documents: IDocument[] | undefined;
    claims: (ProviderClaim | SupportClaim)[] | undefined;

    getCategoryName?: (categoryId: string) => string | undefined;
    getUserName?: (userId: string) => string | undefined;
    /**
     * Resolve the claims a task links to, when the host app can answer better than a fetch.
     *
     * `ClaimCard` otherwise calls `listProviderClaims` itself. In demo mode that request is refused
     * at the boundary, the catch empties the list, and the panel reports "No claims linked" beside a
     * row that is plainly showing a Claim ID — a REQUEST failure rendered as a statement about the
     * data (NS-3). The provider app passes a resolver that goes through `claimStore`, which already
     * branches on `features.isDemo`.
     *
     * Optional: the support console does not supply one and keeps the fetch.
     */
    onClaimsResolve?: (claimIds: string[]) => Promise<(ProviderClaim | SupportClaim)[]>;
    /** See `IMessagingContextProps.onCodingsResolve` (FE-81). */
    onCodingsResolve?: (codingIds: string[]) => Promise<(ProviderCoding | SupportCoding)[]>;
    onClaimSearch: (term: string) => Promise<(ProviderClaim | SupportClaim)[]>;
    onCommentToggle?: (mid: string, cid: string, note: boolean) => Promise<void>;
    onComposeComment?: (comment: IComment) => Promise<void>;
    onComposeMessage?: (message: IMessage) => Promise<void | string>;
    onDocumentDownload: (documentId: string) => Promise<void>;
    onDocumentActualDownload?: (documentId: string) => Promise<string>;
    onDocumentAssign?: (document: IDocument) => Promise<void>;
    onDocumentEdit?: (document: IDocument) => Promise<void>;
    onMarkAsClosed?: (ids: string | string[]) => Promise<void>;
    onMarkAsOpen?: (ids: string | string[]) => Promise<void>;
    onMarkAsRead?: (ids: string | string[]) => Promise<void>;
    onMarkAsUnread?: (ids: string | string[]) => Promise<void>;
    onProviderMarkAsClosed?: (id: string) => Promise<void>;
    onProviderMarkAsOpen?: (id: string) => Promise<void>;
    onMessageClose: () => Promise<void>;
    onMessageOpen?: (message: ProviderMessage | SupportMessage) => Promise<void>;
    /** Mark message done and open the next inbox message (or close panel if none). */
    onCloseAndGoToNext?: (id: string) => Promise<void>;
    onSubjectUpdate?: (id: string, subject: string) => Promise<void>;
    onClaimUpdate?: (id: string, claimId: string | null) => Promise<void>;
    onLinkEntityToMessage?: (id: string, entityType: MessageLinkEntityType, entityIds: string[]) => Promise<void>;
    onUnlinkEntityFromMessage?: (id: string, entityType: MessageLinkEntityType, entityIds: string[]) => Promise<void>;
    onUpdateMessage?: (id: string, payload: { priority?: string; dueDate?: string | null }) => Promise<void>;
    onMessageFieldsUpdate?: () => void;
}

export const MessagingContext = React.createContext<IMessagingContextProps>({
    isReadOnly: true,
    isSubjectEditable: false,
    isListLoading: false,
    isDetailLoading: false,
    enableMessageTypeValidation: false,
    isReadOnlyClaimChoice: false,

    account: undefined,
    message: undefined,
    providerMessage: undefined,
    comments: undefined,
    documents: undefined,
    claims: undefined,

    onClaimSearch: noopClaimSearch,
    onCommentToggle: noop,
    onComposeComment: noop,
    onComposeMessage: noop,
    onDocumentDownload: noop,
    onDocumentAssign: noop,
    onMarkAsClosed: noop,
    onMarkAsOpen: noop,
    onMarkAsRead: noop,
    onMarkAsUnread: noop,
    onMessageClose: noop,
    onMessageOpen: noop,
    onSubjectUpdate: noop,
    onClaimUpdate: noop,
});

export const MessagingProvider: React.FunctionComponent<IMessageProviderProps> = React.memo<IMessageProviderProps>(
    props => {
        const {
            children,
            getCategoryName,
            getUserName,
            onClaimSearch,
            onClaimsResolve,
            onCodingsResolve,
            onCommentToggle,
            onComposeComment,
            onComposeMessage,
            onDocumentDownload,
            onDocumentActualDownload,
            onDocumentAssign,
            onDocumentEdit,
            onMarkAsClosed,
            onMarkAsOpen,
            onMarkAsRead,
            onMarkAsUnread,
            onProviderMarkAsClosed,
            onProviderMarkAsOpen,
            onMessageClose,
            onMessageOpen,
            onCloseAndGoToNext,
            onSubjectUpdate,
            onClaimUpdate,
            onLinkEntityToMessage,
            onUnlinkEntityFromMessage,
            onUpdateMessage,
            onMessageFieldsUpdate,
            isReadOnly,
            isSubjectEditable,
            isListLoading,
            isDetailLoading,
            enableMessageTypeValidation,
            isReadOnlyClaimChoice,
            account,
            message,
            providerMessage,
            comments,
            documents,
            claims,
        } = props;

        // Stable context identity — avoids re-rendering every chat consumer on unrelated parent updates.
        const context = useMemo(
            () => ({
                isReadOnly,
                isSubjectEditable,
                isListLoading,
                isDetailLoading,
                enableMessageTypeValidation,
                isReadOnlyClaimChoice,
                account,
                message,
                providerMessage,
                comments,
                documents,
                claims,
                getCategoryName,
                getUserName,
                onClaimSearch: onClaimSearch || noopClaimSearch,
                onClaimsResolve,
                onCodingsResolve,
                onCommentToggle: onCommentToggle || noop,
                onComposeComment: onComposeComment || noop,
                onComposeMessage: onComposeMessage || noop,
                onDocumentDownload: onDocumentDownload || noop,
                onDocumentActualDownload: onDocumentActualDownload || undefined,
                onDocumentAssign: onDocumentAssign || noop,
                onDocumentEdit: onDocumentEdit || undefined,
                onMarkAsClosed: onMarkAsClosed || noop,
                onMarkAsOpen: onMarkAsOpen || noop,
                onMarkAsRead: onMarkAsRead || noop,
                onMarkAsUnread: onMarkAsUnread || noop,
                onProviderMarkAsClosed: onProviderMarkAsClosed || undefined,
                onProviderMarkAsOpen: onProviderMarkAsOpen || undefined,
                onMessageClose: onMessageClose || noop,
                onMessageOpen: onMessageOpen || noop,
                onCloseAndGoToNext,
                onSubjectUpdate: onSubjectUpdate || noop,
                onClaimUpdate: onClaimUpdate || noop,
                onLinkEntityToMessage: onLinkEntityToMessage || noop,
                onUnlinkEntityFromMessage: onUnlinkEntityFromMessage || noop,
                onUpdateMessage,
                onMessageFieldsUpdate: onMessageFieldsUpdate || undefined,
            }),
            [
                isReadOnly,
                isSubjectEditable,
                isListLoading,
                isDetailLoading,
                enableMessageTypeValidation,
                isReadOnlyClaimChoice,
                account,
                message,
                providerMessage,
                comments,
                documents,
                claims,
                getCategoryName,
                getUserName,
                onClaimSearch,
                onClaimsResolve,
                onCodingsResolve,
                onCommentToggle,
                onComposeComment,
                onComposeMessage,
                onDocumentDownload,
                onDocumentActualDownload,
                onDocumentAssign,
                onDocumentEdit,
                onMarkAsClosed,
                onMarkAsOpen,
                onMarkAsRead,
                onMarkAsUnread,
                onProviderMarkAsClosed,
                onProviderMarkAsOpen,
                onMessageClose,
                onMessageOpen,
                onCloseAndGoToNext,
                onSubjectUpdate,
                onClaimUpdate,
                onLinkEntityToMessage,
                onUnlinkEntityFromMessage,
                onUpdateMessage,
                onMessageFieldsUpdate,
            ],
        );
        return <MessagingContext.Provider value={context}>{children}</MessagingContext.Provider>;
    },
);
